import { useCallback, useState } from "react";
import type { AddMessageInput, AddMessageResponse } from "@mattoboard/schemas";
import { getAuthHeader, useCreditWorkspace } from "ui";
import { useMattoState } from "../MattoState";
import { useLoginState } from "../studioxr/auth/LoginState";
import { processExpressStreamIncremental } from "../design_stream_evt/api/sseStream";
import {
  buildChatRequestBody,
  buildCreateConceptInput,
  type BoardDataInput,
  type SketchRenderRequest,
} from "./conceptRequestBodies";

export type { SketchRenderRequest } from "./conceptRequestBodies";

type AddMessageFn = (input: AddMessageInput) => Promise<AddMessageResponse>;

// A render the backend never ran (unreachable, or non-OK before the stream
// started) — nothing was charged, and a retry usually works.
const RENDER_UNAVAILABLE_TEXT: Record<SketchRenderRequest["kind"], string> = {
  sketchup:
    "We couldn't process your 3D model right now. You haven't been charged a credit — please try again in a few minutes.",
  drawing:
    "We couldn't process your drawing right now. You haven't been charged a credit — please try again in a few minutes.",
};

/**
 * Hook for creating a new concept with the full generation flow.
 * Uses add_message for both PENDING state creation and FINISHED versions.
 *
 * Returns `startConceptGeneration` which creates a PENDING concept immediately
 * and runs generation in the background. The caller can navigate away immediately
 * after receiving the concept_id - the real-time Firestore listener will update
 * the UI when generation completes.
 */
export function useCreateConceptWithAddMessage(addMessage: AddMessageFn) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Workspace context — stamped onto the chat request so the backend's
  // FINISHED message write deducts from the right wallet (Phase A.2a.1).
  const profile = useLoginState((s) => s.profile);
  const permissions = useLoginState((s) => s.permissions);
  // Stamp the new concept with the credit workspace so the backend deducts
  // from the right wallet. When workspaces are gated in production this pins
  // team designers to their team wallet (pre-workspaces behavior) instead of
  // silently charging Personal. See useCreditWorkspace.
  const creditWorkspace = useCreditWorkspace(profile?.uid ?? null, permissions);
  const designFirmId =
    creditWorkspace.kind === "team" ? creditWorkspace.firmId : null;

  /**
   * Run the generation flow in background (after PENDING concept is created).
   * This function does not throw - errors are recorded to Firestore.
   * Streams SSE events to Zustand store so ConceptPage can display real-time updates.
   */
  const runGenerationInBackground = useCallback(
    async (
      conceptId: string,
      brief: string,
      images: Array<{ url: string; tag?: string; viewpointUrl?: string }>,
      paletteColors: Array<{ l: number; a: number; b: number }>,
      searchApiBaseUrl: string,
      boardData?: BoardDataInput,
      options?: { useUploadFlow?: boolean; sketchRender?: SketchRenderRequest },
    ) => {
      // Signal to ConceptPage that initial streaming has started
      const store = useMattoState.getState();
      store.startInitialStream(conceptId);

      const sketchRender = options?.useUploadFlow
        ? options.sketchRender
        : undefined;
      // True until the backend accepts the request: the FAILED write then
      // gets the friendly, coded copy.
      let renderUnavailable = Boolean(sketchRender);

      try {
        // Call Express to generate
        const response = await fetch(
          `${searchApiBaseUrl}/api/design-stream/chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(await getAuthHeader()),
            },
            body: JSON.stringify(
              buildChatRequestBody({
                conceptId,
                brief,
                images,
                paletteColors,
                designFirmId,
                boardData,
                useUploadFlow: options?.useUploadFlow,
                sketchRender: options?.sketchRender,
              }),
            ),
          },
        );

        if (!response.ok) {
          throw new Error(`Generation failed: ${response.status}`);
        }
        renderUnavailable = false;

        // Process response incrementally, pushing SSE events to Zustand store
        const streamResult = await processExpressStreamIncremental(response, {
          onToken: (text) =>
            useMattoState.getState().appendInitialStreamText(text),
          onStatus: (status) =>
            useMattoState.getState().setInitialStreamStatus(status.message),
          onPayload: (payload) => {
            if (payload.type === "interim_description") {
              useMattoState.getState().setInitialStreamInterimDescription({
                content: payload.content,
                keywords: payload.keywords,
              });
            } else {
              useMattoState.getState().appendInitialStreamPayload(payload);
            }
          },
          onError: (errorText) =>
            useMattoState
              .getState()
              .setInitialStreamStatus(`Error: ${errorText}`),
        });

        // Handle different response types
        if (streamResult.type === "error") {
          throw new Error(streamResult.errorMessage || "Generation failed");
        }

        if (streamResult.type === "misc_response") {
          // Backend chat endpoint already persists this assistant message.
          // Do not write again from frontend; avoid duplicate misc responses.
          return;
        }

        if (streamResult.type === "generated_concept") {
          // Backend now persists generated_concept to Firestore directly (via
          // persistGeneratedConcept in chat.ts) before ending the SSE stream.
          // The Firestore listener on ConceptPage picks them up automatically,
          // including after a page refresh when the stream was never received.
          return;
        }

        if (streamResult.type === "generated_board") {
          // Board-generation flow: the classifier routed this brief to the
          // material-board path. The backend has already persisted a
          // BOARD_GENERATE concept_version on `conceptId` with `img_url`,
          // `name` (title), and `segments_url` set. The board editor
          // (`AutoComposeFromCollection` → `autoBoardCompose` branch) reads
          // everything from that version via the Firestore listener — no
          // localStorage handoff needed.
          //
          // Pass the project along in the URL too (#3669 review): when the
          // brief was submitted from /home/concepts/new?project=<id>, carry it
          // so SaveButton can associate the composed board on first save.
          const projectId = new URLSearchParams(window.location.search).get(
            "project",
          );
          window.location.assign(
            `/board/new?conceptId=${encodeURIComponent(
              conceptId,
            )}&autoBoardCompose=1${
              projectId ? `&project=${encodeURIComponent(projectId)}` : ""
            }`,
          );
          return;
        }

        if (streamResult.type === "empty") {
          throw new Error("No generated concept returned from generation");
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error("Concept generation failed:", error.message);

        // Scenario 2: Generation failed after PENDING created - write FAILED state
        const text =
          renderUnavailable && sketchRender
            ? RENDER_UNAVAILABLE_TEXT[sketchRender.kind]
            : error.message;
        try {
          await addMessage({
            concept_id: conceptId,
            message: {
              role: "system",
              content: [{ type: "output_text", text }],
              state: "FAILED",
              // Coded so getUploadFailureMessage shows it in the chat.
              ...(renderUnavailable
                ? { error_code: "SKETCH_RENDER_FAILED" }
                : {}),
              error_message: error.message,
              structured_output: {
                error: { message: error.message, retryable: true },
              },
            },
          });
        } catch (recordErr) {
          // Log but don't throw - the original error is more important
          console.error("Failed to record ERROR state:", recordErr);
        }
      } finally {
        // Clear stream AFTER versions are written — Firestore listener has already
        // delivered the new versions, so there's no visual gap.
        useMattoState.getState().clearInitialStream();
        setIsGenerating(false);
      }
    },
    [addMessage, designFirmId],
  );

  /**
   * Start concept generation. Creates PENDING concept immediately and returns.
   * Generation continues in background - use Firestore real-time listeners to
   * track progress.
   *
   * Error handling:
   * - Scenario 1: PENDING creation fails -> throws error, no concept exists, caller handles UI
   * - Scenario 2: Generation fails after PENDING -> ERROR state written to Firestore
   */
  const startConceptGeneration = useCallback(
    async (
      brief: string,
      images: Array<{ url: string; tag?: string; viewpointUrl?: string }>,
      paletteColors: Array<{ l: number; a: number; b: number }>,
      searchApiBaseUrl: string,
      boardData?: BoardDataInput,
      options?: {
        useUploadFlow?: boolean;
        projectId?: string | null;
        sketchRender?: SketchRenderRequest;
      },
    ): Promise<{ concept_id: string }> => {
      setIsGenerating(true);
      setError(null);

      let conceptId: string | null = null;

      try {
        // Step 1: Create PENDING concept (this is the only awaited part)
        const pendingResult = await addMessage(
          buildCreateConceptInput({
            brief,
            images,
            paletteColors,
            designFirmId,
            isUpload: Boolean(options?.useUploadFlow),
            projectId: options?.projectId,
          }),
        );

        conceptId = pendingResult.concept_id;

        // Step 2: Start generation in background (fire and forget)
        // Don't await the full response - let it run while user sees the PENDING concept
        // But we need to ensure the fetch request is initiated before navigating away
        const generationPromise = runGenerationInBackground(
          conceptId,
          brief,
          images,
          paletteColors,
          searchApiBaseUrl,
          boardData,
          options,
        );

        // Give the fetch a moment to initiate before returning
        // This prevents the navigation from cancelling the request before it starts
        await Promise.race([
          generationPromise,
          new Promise((resolve) => setTimeout(resolve, 100)),
        ]);

        return { concept_id: conceptId };
      } catch (err) {
        // Scenario 1: PENDING creation failed - no concept exists
        // Reset state and re-throw so caller can handle UI
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setIsGenerating(false);
        throw error;
      }
    },
    [addMessage, runGenerationInBackground, designFirmId],
  );

  return { startConceptGeneration, isGenerating, error };
}
